import { useEffect, useState } from 'react';

import {
  COMMUNITY_PUSH_CHANGED,
  getCommunityPushRegistration,
  isAcknowledgedCommunityPush,
  isCommunityPushPath,
} from '~/utils/communityAppAdoption';
import { createBrowserPushSubscription, removeBrowserPushSubscription } from '~/utils/pushSubscription';

export type PushObject = {
  /**
   * Boolean state indicating whether the user is subscribed to push notifications or not.
   */
  isSubscribed: boolean;
  setIsSubscribed: (value: boolean) => void;
  /**
   * Request permission for push notifications
   * @returns The permission status of the push notifications
   */
  requestPermission: () => NotificationPermission;
  /**
   * Subscribe to push notifications in the browser (caller registers it with the server).
   * Resolves the created subscription, or null when push is unavailable; rejects if subscribe() fails.
   */
  subscribeToPush: (publicKey: string) => Promise<PushSubscription | null>;
  /**
   * Unsubscribe from push notifications in the browser (caller deletes it on the server).
   * Resolves the removed subscription, or null when there was none.
   */
  unsubscribeFromPush: () => Promise<PushSubscription | null>;
  /**
   * Boolean state indicating whether the user has allowed sending of push notifications or not.
   */
  canSendPush: boolean;
};

/**
 * Push API hook - contains all your necessities for handling push notifications in the client
 */
export const usePush = (): PushObject => {
  const [swRegistration, setSWRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [canSendPush, setCanSendPush] = useState<boolean>(false);

  const requestPermission = () => {
    if (canSendPush) return 'granted';

    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        setCanSendPush(true);
        return permission;
      }

      setCanSendPush(false);
      return permission;
    });

    return 'default';
  };

  const subscribeToPush = async (publicKey: string): Promise<PushSubscription | null> => {
    const subscription = await createBrowserPushSubscription(publicKey);

    return subscription;
  };

  const unsubscribeFromPush = async (): Promise<PushSubscription | null> => {
    const subscription = await removeBrowserPushSubscription();
    setIsSubscribed(false);
    return subscription;
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const getRegistration = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const _registration = await (isCommunityPushPath()
            ? getCommunityPushRegistration()
            : navigator.serviceWorker.getRegistration());
          setSWRegistration(_registration ?? null);
        } catch (err) {
          console.error('Error getting service worker registration:', err);
        }
      } else {
        console.warn('Service Workers are not supported in this browser.');
      }
    };

    const handleControllerChange = () => {
      getRegistration();
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    getRegistration();

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);

  useEffect(() => {
    if (!swRegistration?.pushManager || !('Notification' in window)) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const subscription = await swRegistration.pushManager.getSubscription();
        const acknowledged = subscription && (await isAcknowledgedCommunityPush(subscription));
        if (!cancelled) {
          setIsSubscribed(Boolean(acknowledged && Notification.permission === 'granted'));
          setCanSendPush(Notification.permission === 'granted');
        }
      } catch {
        // Failed browser inspection does not establish a successful opt-in.
      }
    };
    void refresh();
    window.addEventListener(COMMUNITY_PUSH_CHANGED, refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(COMMUNITY_PUSH_CHANGED, refresh);
    };
  }, [swRegistration]);

  return {
    isSubscribed,
    setIsSubscribed,
    requestPermission,
    subscribeToPush,
    unsubscribeFromPush,
    canSendPush,
  };
};
